
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  EmptyComponent,
  InputComponent,
  SearchComponent,
  DialogService,
  TableComponent,
  type ActionEvent,
  type SortChangeEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import { IngestProjectService } from '../../../../../data-access/src/lib/ingest/ingest-project.service';
import {
  IngestProject,
  IngestProjectCreate,
  IngestProjectUpdate,
} from '../../../../../domain/src/lib/ingest/ingest-project.model';
import type {
  ExtendedIngestProjectsTableStore,
  IngestProjectRow,
} from '../../../../../domain/src/lib/ingest/ingest-store.model';
import { ingestProjectsTableStore } from '../../../../../store/src/lib/ingest-projects-table.store';
import { ingestUiStore } from '../../../../../store/src/lib/ingest-ui.store';
import { openIngestConfirmDialog } from '../ingest-dialog.util';
import { ingestInactiveProjectMessage, isIngestProjectEligibleForUpload } from '../ingest-project-active.util';
import {
  buildIngestProjectCreatedToast,
  buildIngestProjectUpdatedToast,
} from '../ingest-project-feedback.util';
import { INGEST_MODULE_ENV, VaultAppToastService, readVaultItemsPerPageFromPreferences, VAULT_TABLE_PAGE_SIZE_OPTIONS } from '@nebula/shared-vault/data-access';
import { MOCK_INGEST_PROJECTS } from '../ingest-ui-mock.data';
import { ServerContextService } from '../../servers/server-context.service';
import { Subject, catchError, filter, finalize, map, of, switchMap, timeout } from 'rxjs';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderAction,
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultFormValidationAlertComponent,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
  scrollVaultEditPanelWhenReady,
} from '@nebula-shared/ui';
import { subscribeVaultTablePageSizePreference } from '../../vault-table-pagination-bind.util';
import {
  INGEST_PROJECT_NAME_LENGTH_ERROR,
  INGEST_PROJECT_NAME_MAX_LENGTH,
  INGEST_PROJECT_NAME_TOOLTIP,
  ingestProjectNameNeedsPutAfterCreate,
  toIngestProjectCreateName,
  toIngestProjectFolderPath,
  validateIngestProjectName,
} from '../ingest-project-name.util';
import {
  INGEST_NEW_PROJECT_HEADER_ACTION,
  closeIngestCreatePanel,
  closeIngestCreatePanelAfterSuccess,
  closeIngestCreatePanelForEdit,
  emptyIngestProjectCreateForm,
  ingestProjectsEmptyListHint,
  openIngestCreatePanel,
  shouldHandleIngestNewProjectHeaderAction,
  type IngestProjectCreateFormFields,
} from '../ingest-project-create-panel.util';

type IngestProjectFormFields = IngestProjectCreateFormFields;

@Component({
  selector: 'app-ingest-projects',
  standalone: true,
  imports: [
    BadgeComponent,
    ButtonComponent,
    InputComponent,
    CheckboxComponent,
    EmptyComponent,
    SearchComponent,
    TableComponent,
    PageHeaderComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    VaultFormValidationAlertComponent,
    VaultModuleShellComponent,
    VaultPageAlertComponent,
  ],
  templateUrl: './ingest-projects.component.html',
  styleUrls: ['./ingest-projects.component.scss'],
})
export class IngestProjectsComponent implements OnInit {
  private static readonly MODULE_LABEL = 'Carga de Documentos';
  private static readonly PROJECT_DESCRIPTION_MAX_LENGTH = 100;

  readonly projectNameMaxLength = INGEST_PROJECT_NAME_MAX_LENGTH;
  readonly projectDescriptionMaxLength = IngestProjectsComponent.PROJECT_DESCRIPTION_MAX_LENGTH;
  readonly projectNameTooltip = INGEST_PROJECT_NAME_TOOLTIP;

  private readonly ingestProjectService = inject(IngestProjectService);
  private readonly ingestEnv = inject(INGEST_MODULE_ENV);
  private readonly serverContext = inject(ServerContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uiStore = inject(ingestUiStore);
  private readonly dialogService = inject(DialogService);
  private readonly vaultAppToast = inject(VaultAppToastService);

  readonly tableStore = inject(ingestProjectsTableStore) as unknown as ExtendedIngestProjectsTableStore;
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];

  /** Referencia para alinear el `pageIndex` interno de `lib-table` con el store al remontar la vista. */
  private readonly projectsTable = viewChild(TableComponent<IngestProjectRow>);
  private readonly createPanel = viewChild<ElementRef<HTMLElement>>('createPanel');
  private readonly editPanel = viewChild<ElementRef<HTMLElement>>('editPanel');

  private readonly injector = inject(Injector);

  /** Cancela listados en curso si se dispara otra carga o se destruye el componente. */
  private readonly loadProjectsTrigger$ = new Subject<void>();

  /**
   * El MatPaginator emite (page) al montar (a menudo índice 0) y eso pisaba el store.
   * Mientras restauramos desde el store, no escribimos en él por esos eventos.
   */
  private paginatorRestoreLocked = false;

  /**
   * Tras `table.pageIndex.set(...)` programático, a veces se emite otro (page): lo ignoramos una vez.
   */
  private suppressPaginatorStoreWrite = 0;

  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  /** Errores de acciones de tabla (activar, desactivar, etc.). */
  readonly error = signal<string | null>(null);
  readonly newProjectNameError = signal<string | null>(null);
  readonly editProjectNameError = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly creating = signal(false);
  readonly emptyListHint = ingestProjectsEmptyListHint;
  readonly headerActions = computed<PageHeaderAction[]>(() => [
    {
      id: INGEST_NEW_PROJECT_HEADER_ACTION,
      label: 'Nuevo expediente',
      icon: 'Plus',
      variant: 'primary',
      disabled: this.tableStore.loading(),
    },
    {
      id: 'upload',
      label: 'Carga de documentos (ingesta)',
      icon: 'Upload',
      variant: 'secondary',
      disabled: this.tableStore.loading(),
    },
    {
      id: 'trazabilidad',
      label: 'Trazabilidad de preservación',
      icon: 'Activity',
      variant: 'secondary',
    },
    {
      id: 'reload',
      label: 'Actualizar',
      icon: 'RefreshCcw',
      variant: 'secondary',
      loading: this.tableStore.loading(),
    },
  ]);

  readonly tableConfig = signal<TableConfig<IngestProjectRow>>({
    columns: [
      { key: 'name', label: 'Nombre', sortable: true, align: 'left', cellTemplate: true },
      { key: 'folder_path', label: 'Carpeta', sortable: true, align: 'left', cellTemplate: true },
      { key: 'description', label: 'Descripción', sortable: false, align: 'left', cellTemplate: true },
      {
        key: 'activeLabel',
        label: 'Estado',
        sortable: true,
        align: 'center',
        width: '110px',
        cellTemplate: true,
      },
    ],
    actions: [
      { label: 'Ver', icon: 'view', action: 'view' },
      { label: 'Editar', icon: 'edit', action: 'edit' },
      {
        label: 'Desactivar',
        icon: 'LockOpen',
        action: 'deactivate',
        color: 'warn',
        visible: (row) => row.is_active,
      },
      {
        label: 'Activar',
        icon: 'Lock',
        action: 'activate',
        visible: (row) => !row.is_active,
      },
    ],
    showPagination: true,
    rowClickable: true,
    defaultAction: 'view',
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
    initialSort: { column: 'name', direction: 'asc' },
  });

  newProject: IngestProjectFormFields = emptyIngestProjectCreateForm();

  editingId: number | null = null;
  editForm: IngestProjectFormFields = emptyIngestProjectCreateForm();

  constructor() {
    this.loadProjectsTrigger$
      .pipe(
        switchMap(() => this.fetchProjects$()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (rows) => {
          this.beginPaginatorRestore();
          this.tableStore.loadProjects(rows);
          this.syncEditFormActiveStateFromList(rows);
          this.scheduleApplyStorePageToTable();
        },
        error: (err) => {
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: IngestProjectsComponent.MODULE_LABEL,
              fallback: 'No se pudieron cargar los expedientes.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          );
        },
      });
    subscribeVaultTablePageSizePreference(this.destroyRef, (n) => this.tableStore.setPageSize(n));
  }

  ngOnInit(): void {
    // El tama\u00f1o guardado en el store (sessionStorage) puede quedar desfasado del
    // ajuste global de Configuraci\u00f3n; al entrar al m\u00f3dulo se re-aplica la preferencia.
    this.tableStore.setPageSize(readVaultItemsPerPageFromPreferences());
    const tenantId = this.serverContext.getContext().tenantId;
    if (!tenantId) {
      this.info.set(
        'Tip: configura el Tenant UUID en "Configuración Servidores" para usar el backend oficial.',
      );
    }
    this.restoreProjectsDrafts();
    this.loadProjects();
  }

  private restoreProjectsDrafts(): void {
    const drafts = this.uiStore.projectsDraft();
    this.newProject = { ...drafts.newProject };
    this.editingId = drafts.editingId;
    this.editForm = { ...drafts.editForm };
  }

  private toRow(project: IngestProject): IngestProjectRow {
    return {
      ...project,
      activeLabel: project.is_active ? 'Activo' : 'Inactivo',
    };
  }

  loadProjects(): void {
    this.resetProjectsLoadErrorState();
    this.loadProjectsTrigger$.next();
  }

  private resetProjectsLoadErrorState(): void {
    this.error.set(null);
    this.newProjectNameError.set(null);
    this.editProjectNameError.set(null);
    this.info.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.loadErrorTitle.set('');
  }

  private fetchProjects$() {
    if (this.ingestEnv.previewMode) {
      return of(MOCK_INGEST_PROJECTS.map((p) => this.toRow({ ...p })));
    }

    this.tableStore.startLoading();
    return this.ingestProjectService.list().pipe(
      timeout(45000),
      map((list) => (list ?? []).map((p) => this.toRow(p))),
      finalize(() => this.tableStore.setLoading(false)),
    );
  }

  onSearchTermChange(term: string): void {
    this.tableStore.setSearchTerm(term);
  }

  onNewProjectField<K extends keyof IngestProjectFormFields>(key: K, value: IngestProjectFormFields[K]): void {
    this.newProject = { ...this.newProject, [key]: value };
    if (key === 'name') {
      this.newProjectNameError.set(null);
    }
    this.uiStore.setProjectsNewDraft(this.newProject);
  }

  onEditProjectField<K extends keyof IngestProjectFormFields>(key: K, value: IngestProjectFormFields[K]): void {
    const nextValue =
      key === 'is_active' ? (value === true) : value;
    this.editForm = { ...this.editForm, [key]: nextValue };
    if (key === 'name') {
      this.editProjectNameError.set(null);
    }
    this.uiStore.setProjectsEditDraft(this.editingId, this.editForm);
  }

  onTableSort(ev: SortChangeEvent<IngestProjectRow>): void {
    this.tableStore.setSort(
      ev.column as keyof IngestProjectRow | null,
      ev.direction,
    );
  }

  onPageChange(ev: { pageIndex: number; pageSize: number; length?: number }): void {
    if (this.paginatorRestoreLocked) {
      return;
    }
    if (this.suppressPaginatorStoreWrite > 0) {
      this.suppressPaginatorStoreWrite -= 1;
      return;
    }
    if (ev?.pageSize != null) {
      this.tableStore.setPageSize(ev.pageSize);
    }
    if (ev?.pageIndex != null) {
      this.tableStore.setPageIndex(ev.pageIndex);
    }
  }

  /** Antes de que el store actualice la vista y monte el paginador (emisión espuria a índice 0). */
  private beginPaginatorRestore(): void {
    this.paginatorRestoreLocked = true;
  }

  /**
   * Tras pintar `lib-table` + paginador, aplica la página del store.
   * Reintenta si el ViewChild aún no existe; solo quita el lock cuando hubo sync o se agotan intentos.
   */
  private scheduleApplyStorePageToTable(): void {
    afterNextRender(
      () => {
        this.tryApplyStorePageToTable(0);
      },
      { injector: this.injector },
    );
  }

  private readonly maxPaginatorApplyAttempts = 12;

  private tryApplyStorePageToTable(attempt: number): void {
    const delay = attempt === 0 ? 0 : 24;
    setTimeout(() => {
      const table = this.projectsTable();
      const loading = this.tableStore.loading();

      if (loading && attempt < this.maxPaginatorApplyAttempts) {
        this.tryApplyStorePageToTable(attempt + 1);
        return;
      }
      if (!table && attempt < this.maxPaginatorApplyAttempts) {
        this.tryApplyStorePageToTable(attempt + 1);
        return;
      }

      try {
        if (table && !loading) {
          this.applyStorePageToTable();
        }
      } finally {
        this.paginatorRestoreLocked = false;
      }
    }, delay);
  }

  private applyStorePageToTable(): void {
    const table = this.projectsTable();
    if (!table || this.tableStore.loading()) return;

    const count = table.totalFilteredCount();
    const ps = Math.max(1, table.pageSize());
    const maxIdx = Math.max(0, Math.ceil(count / ps) - 1);
    const want = Math.min(Math.max(0, this.tableStore.pageIndex()), maxIdx);

    if (table.pageIndex() === want) {
      if (want !== this.tableStore.pageIndex()) {
        untracked(() => this.tableStore.setPageIndex(want));
      }
      return;
    }

    this.suppressPaginatorStoreWrite += 1;
    table.pageIndex.set(want);
    if (want !== this.tableStore.pageIndex()) {
      untracked(() => this.tableStore.setPageIndex(want));
    }
  }

  private validateProjectName(name: string | undefined | null): string | null {
    return validateIngestProjectName(name);
  }

  private isProjectNameFieldError(message: string): boolean {
    return /nombre del proyecto|nombre del expediente|solo puede contener|guiones bajos|caracteres|obligatorio|máximo|máx\.|tilde|acento/i.test(
      message,
    );
  }

  private normalizeProjectNameError(message: string, name?: string): string {
    const trimmed = name?.trim() ?? '';
    if (
      trimmed.length > INGEST_PROJECT_NAME_MAX_LENGTH ||
      /excede|exceder|superar|too long|máximo.*caracter|max.*length/i.test(message)
    ) {
      return INGEST_PROJECT_NAME_LENGTH_ERROR;
    }
    // Si el API habla de tildes/acentos, mostrar su mensaje (no ocultar la causa).
    if (/tilde|acento|diacrít|ascii|sin caracteres especiales/i.test(message)) {
      return message;
    }
    // No remapeamos a un texto que dice "tildes OK" si el API rechazó el nombre.
    if (/solo puede contener|guiones bajos|letras.*números/i.test(message)) {
      return message;
    }
    if (/obligatorio|required/i.test(message)) {
      return 'El nombre es obligatorio.';
    }
    return message;
  }

  private applyProjectFormError(
    err: unknown,
    form: 'new' | 'edit',
    fallback: string,
  ): void {
    const message = toModuleAccessErrorMessage(err, {
      moduleLabel: 'Carga de Documentos',
      fallback,
    });
    if (this.isProjectNameFieldError(message)) {
      const normalized = this.normalizeProjectNameError(
        message,
        form === 'new' ? this.newProject.name : this.editForm.name,
      );
      if (form === 'new') {
        this.newProjectNameError.set(normalized);
      } else {
        this.editProjectNameError.set(normalized);
      }
      return;
    }
    this.error.set(message);
  }

  createProject(): void {
    const nameError = this.validateProjectName(this.newProject.name);
    if (nameError) {
      this.newProjectNameError.set(nameError);
      return;
    }
    const displayName = this.newProject.name.trim();
    this.error.set(null);
    this.newProjectNameError.set(null);
    this.info.set(null);

    // POST del API suele rechazar tildes en `name`; PUT (editar) sí las acepta.
    // Creamos con nombre/carpeta ASCII y, si hace falta, actualizamos el nombre visible.
    const createName = toIngestProjectCreateName(displayName);
    const folderPath =
      this.newProject.folder_path?.trim() || toIngestProjectFolderPath(displayName);
    const body: IngestProjectCreate = {
      name: createName,
      description: this.newProject.description || null,
      folder_path: folderPath,
      is_active: this.newProject.is_active ?? true,
    };

    if (this.ingestEnv.previewMode) {
      const list = this.tableStore.projects();
      const nextId = Math.max(0, ...list.map((p) => p.id)) + 1;
      this.beginPaginatorRestore();
      this.tableStore.loadProjects([
        ...list,
        this.toRow({
          id: nextId,
          name: displayName,
          description: body.description,
          folder_path: body.folder_path?.trim() || createName,
          is_active: body.is_active ?? true,
        }),
      ]);
      this.scheduleApplyStorePageToTable();
      this.vaultAppToast.show(buildIngestProjectCreatedToast(displayName));
      this.applyCreatePanelState(closeIngestCreatePanelAfterSuccess());
      return;
    }

    const resetNewForm = (): void => {
      this.applyCreatePanelState(closeIngestCreatePanelAfterSuccess());
    };

    this.ingestProjectService
      .create(body)
      .pipe(
        switchMap((created) => {
          if (!ingestProjectNameNeedsPutAfterCreate(displayName) || !created?.id) {
            return of(created);
          }
          const updateBody: IngestProjectUpdate = {
            name: displayName,
            description: body.description,
            folder_path: body.folder_path,
            is_active: body.is_active,
          };
          return this.ingestProjectService.update(created.id, updateBody).pipe(
            // Si el PUT falla, el proyecto ya existe con nombre ASCII.
            catchError(() => of({ ...created, name: displayName })),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.vaultAppToast.show(buildIngestProjectCreatedToast(displayName));
          resetNewForm();
          this.loadProjects();
        },
        error: (err) => {
          this.applyProjectFormError(err, 'new', 'No se pudo crear el expediente.');
        },
      });
  }

  onTableAction(ev: ActionEvent<IngestProject>): void {
    switch (ev.action) {
      case 'view':
        this.goToDetail(ev.data.id);
        break;
      case 'edit':
        this.startEdit(ev.data);
        break;
      case 'deactivate':
        this.deactivateProject(ev.data);
        break;
      case 'activate':
        this.activateProject(ev.data);
        break;
    }
  }

  onHeaderAction(actionId: string): void {
    if (shouldHandleIngestNewProjectHeaderAction(actionId)) {
      this.startCreate();
      return;
    }
    switch (actionId) {
      case 'upload':
        this.goToUploadHub();
        break;
      case 'trazabilidad':
        void this.router.navigate(['trazabilidad'], { relativeTo: this.route.parent });
        break;
      case 'reload':
        this.loadProjects();
        break;
    }
  }

  startCreate(): void {
    this.cancelEdit();
    const next = openIngestCreatePanel();
    this.applyCreatePanelState(next);
    afterNextRender(
      () =>
        scrollVaultEditPanelWhenReady(() => this.createPanel()?.nativeElement ?? null),
      { injector: this.injector },
    );
  }

  closeCreateForm(): void {
    this.newProjectNameError.set(null);
    this.applyCreatePanelState(closeIngestCreatePanel());
  }

  private applyCreatePanelState(next: ReturnType<typeof openIngestCreatePanel>): void {
    this.creating.set(next.creating);
    this.newProject = next.form;
    this.uiStore.setProjectsNewDraft(this.newProject);
  }

  startEdit(p: IngestProject): void {
    this.applyCreatePanelState(closeIngestCreatePanelForEdit());
    this.editingId = p.id;
    this.editProjectNameError.set(null);
    this.editForm = {
      name: p.name,
      description: p.description ?? '',
      folder_path: p.folder_path,
      is_active: p.is_active === true,
    };
    this.uiStore.setProjectsEditDraft(this.editingId, this.editForm);
    afterNextRender(
      () =>
        scrollVaultEditPanelWhenReady(() => this.editPanel()?.nativeElement ?? null),
      { injector: this.injector },
    );
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editProjectNameError.set(null);
    this.editForm = emptyIngestProjectCreateForm();
    this.uiStore.clearProjectsEditDraft();
  }

  saveEdit(): void {
    if (this.editingId == null) return;
    const nameError = this.validateProjectName(this.editForm.name);
    if (nameError) {
      this.editProjectNameError.set(nameError);
      return;
    }
    this.error.set(null);
    this.editProjectNameError.set(null);
    this.info.set(null);

    const projectId = this.editingId;
    const live = this.tableStore.projects().find((row) => row.id === projectId);
    if (!live) {
      this.error.set('El expediente ya no está en la lista. Actualice e intente de nuevo.');
      this.cancelEdit();
      this.loadProjects();
      return;
    }
    const wantsActive = this.editForm.is_active === true;
    const body: IngestProjectUpdate = {
      name: this.editForm.name,
      description: this.editForm.description || null,
      folder_path: this.editForm.folder_path || null,
      is_active: wantsActive,
    };

    if (this.ingestEnv.previewMode) {
      const rows = this.tableStore.projects();
      this.beginPaginatorRestore();
      this.tableStore.loadProjects(
        rows.map((p) =>
          p.id === projectId
            ? this.toRow({
                ...p,
                name: body.name!,
                description: body.description,
                folder_path: body.folder_path?.trim() || p.folder_path,
                is_active: wantsActive,
              })
            : p,
        ),
      );
      this.scheduleApplyStorePageToTable();
      this.vaultAppToast.show(buildIngestProjectUpdatedToast(body.name!));
      this.cancelEdit();
      return;
    }

    const finalizeSuccess = () => {
      this.vaultAppToast.show(buildIngestProjectUpdatedToast(body.name!));
      this.cancelEdit();
      // Reconciliar con GET (fuente de verdad; evita ids viejos o duplicados en BD).
      this.loadProjects();
    };

    // Activar/desactivar se persiste vía PUT `is_active` (contrato de ingesta). No usar DELETE
    // para desactivar: el borrado no refleja el estado inactivo y el cambio no se mantiene al refrescar.
    this.ingestProjectService.update(live.id, body).subscribe({
      next: () => finalizeSuccess(),
      error: (err: unknown) => {
        this.applyProjectFormError(err, 'edit', 'No se pudo actualizar el expediente.');
      },
    });
  }

  deactivateProject(p: IngestProject): void {
    openIngestConfirmDialog(this.dialogService, {
      title: 'Desactivar expediente',
      message: `¿Desactivar el expediente «${p.name}»? Quedará en modo solo consulta hasta que lo reactive.`,
      variant: 'warning',
      confirmLabel: 'Desactivar',
      cancelLabel: 'Cancelar',
    })
      .pipe(
        filter((confirmed) => confirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.applyDeactivateProject(p));
  }

  private applyDeactivateProject(p: IngestProject): void {
    this.error.set(null);
    this.info.set(null);

    const live = this.tableStore.projects().find((row) => row.id === p.id);
    if (!live) {
      this.error.set('El expediente ya no está en la lista. Actualice e intente de nuevo.');
      this.loadProjects();
      return;
    }

    if (this.ingestEnv.previewMode) {
      const rows = this.tableStore.projects();
      this.beginPaginatorRestore();
      this.tableStore.loadProjects(
        rows.map((row) =>
          row.id === p.id ? this.toRow({ ...row, is_active: false }) : row,
        ),
      );
      this.scheduleApplyStorePageToTable();
      this.info.set('Expediente desactivado en vista prevía (no persistido).');
      this.cancelEdit();
      return;
    }

    // Desactivar = PUT `is_active=false` (persistente y consistente con activar). El DELETE
    // no dejaba el proyecto en estado inactivo, por eso el cambio no se mantenía al refrescar.
    this.ingestProjectService
      .update(live.id, {
        name: live.name,
        description: live.description ?? null,
        folder_path: live.folder_path ?? null,
        is_active: false,
      })
      .subscribe({
        next: () => {
          this.info.set('Expediente desactivado.');
          this.cancelEdit();
          this.loadProjects();
        },
        error: (err) => {
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Carga de Documentos',
              fallback: 'No se pudo desactivar el expediente.',
            }),
          );
        },
      });
  }

  activateProject(p: IngestProject): void {
    openIngestConfirmDialog(this.dialogService, {
      title: 'Activar expediente',
      message: `¿Activar el expediente «${p.name}»? Volverá a estar habilitado para ingesta de documentos.`,
      variant: 'info',
      confirmLabel: 'Activar',
      cancelLabel: 'Cancelar',
    })
      .pipe(
        filter((confirmed) => confirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.applyActivateProject(p));
  }

  private applyActivateProject(p: IngestProject): void {
    this.error.set(null);
    this.info.set(null);

    const live = this.tableStore.projects().find((row) => row.id === p.id);
    if (!live) {
      this.error.set('El expediente ya no está en la lista. Actualice e intente de nuevo.');
      this.loadProjects();
      return;
    }

    if (this.ingestEnv.previewMode) {
      const rows = this.tableStore.projects();
      this.beginPaginatorRestore();
      this.tableStore.loadProjects(
        rows.map((row) =>
          row.id === p.id ? this.toRow({ ...row, is_active: true }) : row,
        ),
      );
      this.scheduleApplyStorePageToTable();
      this.info.set('Expediente activado en vista prevía (no persistido).');
      this.cancelEdit();
      return;
    }

    this.ingestProjectService
      .update(live.id, {
        name: live.name,
        description: live.description ?? null,
        folder_path: live.folder_path ?? null,
        is_active: true,
      })
      .subscribe({
        next: () => {
          this.info.set('Expediente activado.');
          this.cancelEdit();
          this.loadProjects();
        },
        error: (err) => {
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Carga de Documentos',
              fallback: 'No se pudo activar el expediente.',
            }),
          );
        },
      });
  }

  private applyProjectActiveState(projectId: number, isActive: boolean): void {
    const row = this.tableStore.projects().find((item) => item.id === projectId);
    if (!row) return;
    this.applyEditedProjectToStore(projectId, {
      name: row.name,
      description: row.description ?? null,
      folder_path: row.folder_path,
      is_active: isActive,
    });
  }

  private applyEditedProjectToStore(
    projectId: number,
    patch: Pick<IngestProject, 'name' | 'description' | 'folder_path' | 'is_active'>,
  ): void {
    const rows = this.tableStore.projects();
    this.beginPaginatorRestore();
    this.tableStore.loadProjects(
      rows.map((row) =>
        row.id === projectId ? this.toRow({ ...row, ...patch }) : row,
      ),
    );
    this.scheduleApplyStorePageToTable();
  }

  /** Evita que un borrador persistido deje el check «Activo» desincronizado tras recargar. */
  private syncEditFormActiveStateFromList(rows: IngestProjectRow[]): void {
    if (this.editingId == null) return;
    const live = rows.find((row) => row.id === this.editingId);
    if (!live) {
      this.cancelEdit();
      return;
    }
    if (this.editForm.is_active === (live.is_active === true)) return;
    this.editForm = { ...this.editForm, is_active: live.is_active === true };
    this.uiStore.setProjectsEditDraft(this.editingId, this.editForm);
  }

  goToDetail(id: number): void {
    void this.router.navigate(['projects', id], {
      relativeTo: this.route.parent,
    });
  }

  goToUploadHub(): void {
    void this.router.navigate(['upload'], { relativeTo: this.route.parent });
  }

  goToProjectUpload(id: number): void {
    const project = this.tableStore.projects().find((row) => row.id === id);
    if (project && !isIngestProjectEligibleForUpload(project)) {
      this.error.set(ingestInactiveProjectMessage(project.name));
      return;
    }
    void this.router.navigate(['projects', id, 'upload'], {
      relativeTo: this.route.parent,
    });
  }
}
